import { Component, Inject } from '@angular/core';
import { DialogConfig } from '../dialog/dialog.config';
import { DialogRef } from '../dialog/dialog.ref';
import { ScdPickerBaseValue } from '../scd-picker/scd-picker.component';
import { FormControl, FormGroup } from '@angular/forms';
import {
  ScdPickerExtendedBaseValue,
  ScdPickerSearchQuery,
  ScdPickerTranslations,
} from './scd-picker-extended.types';
import { componentStylesByTheme } from '../utils/theme.utils';
import { Theme, THEME_TOKEN } from '../types/theme.types';
import {
  searchResultStylesSAG,
  searchResultStylesSHS,
  textStylesPrimarySAG,
  textStylesPrimarySHS,
  textStylesSecondarySAG,
  textStylesSecondarySHS,
} from './scd-picker-extended.styles';

export type ScdDialogConfig<T extends ScdPickerExtendedBaseValue> = {
  searchFn: (data: ScdPickerSearchQuery) => Promise<T[]>;
  translations: ScdPickerTranslations;
};

@Component({
  selector: 'sag-ui-scd-picker-dialog',
  template: `
    <div class="p-4">
      <h1 [class]="primaryTextClasses + ' font-bold text-lg p-0'">
        {{ translations.dialog.title }}
      </h1>
      <p [class]="secondaryTextClasses + 'text-md'">
        {{ translations.dialog.subtitle }}
      </p>
      <div
        class="grid grid-cols-[1fr_1fr_1fr_56px] gap-2 mt-4 items-end"
        [formGroup]="formGroup"
      >
        <sag-ui-input
          [label]="translations.dialog.labels.firstName"
          formControlName="firstName"
          [invalid]="!!firstNameError"
          [error]="firstNameError"
        ></sag-ui-input>
        <sag-ui-input
          [label]="translations.dialog.labels.lastName"
          formControlName="lastName"
          [invalid]="!!lastNameError"
          [error]="lastNameError"
        ></sag-ui-input>
        <sag-ui-input label="GID" formControlName="gid"></sag-ui-input>
        <sag-ui-button
          class="h-[42px] w-[42px]"
          (click)="onSearch()"
          [loading]="loading"
          [square]="true"
        >
          <span class="icon-search"></span>
        </sag-ui-button>
      </div>
      <div class="h-[300px] my-4 overflow-y-auto">
        <div class="block max-h-14 overflow-hidden px-4 py-0 text-white">
          <div
            class="grid cursor-pointer grid-cols-4 items-center gap-4 p-2 text-sm"
          >
            <span [class]="secondaryTextClasses + ' truncate text-xs'">{{
              translations.dialog.table.header.name
            }}</span>
            <span [class]="secondaryTextClasses + ' truncate text-xs'">{{
              translations.dialog.table.header.gid
            }}</span>
            <span [class]="secondaryTextClasses + ' truncate text-xs'">{{
              translations.dialog.table.header.department
            }}</span>
            <span [class]="secondaryTextClasses + ' truncate text-xs'">{{
              translations.dialog.table.header.select
            }}</span>
          </div>
        </div>

        @if (loading) {
          <div class="animate-pulse">
            <div
              [class]="
                searchResultsClasses +
                ' h-14 rounded-md opacity-100 w-full mb-1'
              "
            ></div>
            <div
              [class]="
                searchResultsClasses + ' h-14 rounded-md opacity-90 w-full mb-1'
              "
            ></div>
            <div
              [class]="
                searchResultsClasses + ' h-14 rounded-md opacity-80 w-full mb-1'
              "
            ></div>
            <div
              [class]="
                searchResultsClasses + ' h-14 rounded-md opacity-70 w-full mb-1'
              "
            ></div>
            <div
              [class]="
                searchResultsClasses + ' h-14 rounded-md opacity-60 w-full mb-1'
              "
            ></div>
          </div>
        } @else {
          @if (searchResults.length > 0) {
            @for (item of searchResults; track $index) {
              <div
                [class]="
                  primaryTextClasses +
                  ' ' +
                  searchResultsClasses +
                  ' block max-h-14 overflow-hidden rounded-lg px-4 py-0 shadow-sm mb-2'
                "
              >
                <div
                  class="grid h-14 cursor-pointer grid-cols-4 items-center gap-4 p-2 text-sm"
                >
                  <span class="truncate"
                    >{{ item.firstName }} {{ item.lastName }}</span
                  >
                  <span class="truncate">{{ item.gid }}</span>
                  <span class="truncate">{{ item.department }}</span>
                  <span class="flex justify-end items-center"
                    ><sag-ui-button
                      (click)="onSelect(item)"
                      size="sm"
                      icon="chevron-right"
                      iconPosition="end"
                      >{{ translations.dialog.table.select }}</sag-ui-button
                    ></span
                  >
                </div>
              </div>
            }
          } @else {
            <div
              class="text-center text-[#CCCCD4] flex items-center justify-center h-full"
            >
              {{ translations.dialog.noResults }}
            </div>
          }
        }
      </div>
      <div class="flex justify-end items-center gap-2 mt-4">
        <sag-ui-button color="secondary" (click)="onClose()">{{
          translations.dialog.cancel
        }}</sag-ui-button>
      </div>
    </div>
  `,
})
export class ScdPickerDialogComponent<T extends ScdPickerExtendedBaseValue> {
  formGroup = new FormGroup({
    firstName: new FormControl(''),
    lastName: new FormControl(''),
    gid: new FormControl(''),
  });
  searchResults: T[] = [];
  loading = false;
  firstNameError = '';
  lastNameError = '';

  constructor(
    @Inject(THEME_TOKEN) private readonly theme: Theme,
    public config: DialogConfig<ScdDialogConfig<T>>,
    public dialogRef: DialogRef<Partial<ScdPickerBaseValue>>,
  ) {}

  protected get primaryTextClasses() {
    return componentStylesByTheme(
      this.theme,
      textStylesPrimarySAG,
      textStylesPrimarySHS,
    )();
  }

  protected get secondaryTextClasses() {
    return componentStylesByTheme(
      this.theme,
      textStylesSecondarySAG,
      textStylesSecondarySHS,
    )();
  }

  protected get searchResultsClasses() {
    return componentStylesByTheme(
      this.theme,
      searchResultStylesSAG,
      searchResultStylesSHS,
    )();
  }

  protected get translations() {
    return this.config.data!.translations;
  }

  private searchValid() {
    this.firstNameError = '';
    this.lastNameError = '';

    if (this.formGroup.value.gid) {
      return true;
    }

    if (this.formGroup.value.firstName && !this.formGroup.value.lastName) {
      this.lastNameError = this.translations.dialog.errors.lastNameRequired;
      return false;
    }

    if (this.formGroup.value.lastName && !this.formGroup.value.firstName) {
      this.firstNameError = this.translations.dialog.errors.firstNameRequired;
      return false;
    }

    return true;
  }

  onSearch() {
    if (!this.searchValid()) return;

    this.loading = true;
    this.config.data!
      .searchFn(this.formGroup.value)
      .then((results) => {
        this.searchResults = results;
      })
      .finally(() => {
        this.loading = false;
      });
  }

  onSelect(user: ScdPickerBaseValue) {
    this.dialogRef.close(user);
  }

  onClose() {
    this.dialogRef.close();
  }
}
